// Percorso file: src/pages/CoachAttendanceDetail.jsx
//
// LA SCHEDA DI UN ALLENATORE — cantiere COACH-ATTENDANCE, Fetta 3 (frontend).
//
// La pagina con cui l'AMMINISTRATORE valuta UN allenatore: in testa il nome
// e il totale della stagione «X of Y (Z%)», sotto l'elenco delle sue sessioni
// passate — di TUTTE le squadre che allena — con il segno present/absent/—
// e la nota, se c'è. SOLA LETTURA: qui non si segna nulla, la segnatura resta
// nella pagina della squadra (CoachAttendance.jsx, Fetta 2), da cui si arriva
// col clic sul nome. Rotta `/coach-attendance/coach/:coachId`, dietro
// `RequireRole(["ADMIN","SUPERADMIN"])` in App.jsx.
//
// È il calco DICHIARATO di due precedenti:
//   · `PresenzeDelFiglio` (Attendance.jsx) per la LETTURA DELLA PERSONA: id
//     dalla rotta (`useParams`), flag di annullamento, tre stati
//     caricamento/ok/errore, esito UNICO su 403/404/rete e il solo 409 a
//     parte; a documento non letto il nome resta vuoto — dal profilo non
//     trapela nulla, nemmeno il nome;
//   · `PresenzeAllenatori` (CoachAttendance.jsx) per IL CONTO, che usa LO
//     STESSO METRO della griglia (decisione del committente, Fetta 3).
//
// ⚠️ IL METRO DELLA GRIGLIA, e il perché di tre letture. La percentuale della
// griglia è `present / sessioni.length`, dove le sessioni sono TUTTE le
// sessioni passate della stagione, segnate o no: il non segnato abbassa la
// percentuale come un'assenza. La rotta della scheda (`GET
// /coach-attendance/coach/:coachId`, Fetta 1) dà solo i RECORD esistenti, e
// col solo `present + absent` la stessa persona avrebbe due percentuali
// diverse nelle due pagine. Per ricostruire lo stesso Y servono quindi
//   1. GET /users/:coachId       → il nome e le TESSERE (le squadre COACH
//                                  dell'anno, lettore unico `squadreDelSoggetto`);
//   2. GET /events (nudo)        → le sessioni passate di QUELLE squadre
//                                  nell'anno operativo, filtrate come nella
//                                  griglia (`eDiUnaMiaSquadra` + anno + passate);
//   3. GET /coach-attendance/coach/:coachId → i record present/absent.
// «Stesso metro» vuol dire STESSA FORMULA, non stesso numero: la griglia
// conta per UNA squadra, la scheda aggrega su tutte le squadre dell'allenatore.
//
// ⚠️ LE TRE LETTURE SONO TUTTE NECESSARIE: senza il documento non ci sono le
// squadre (e senza squadre Y è zero), senza il calendario non ci sono le
// sessioni. Un guasto di UNA qualsiasi è quindi «la scheda non è disponibile»
// — non un calendario vuoto spacciato per un allenatore senza sessioni, che
// sarebbe fingere una risposta (stessa dottrina di PresenzeDelFiglio: «il
// ripiego è la vista vuota, non la vista piena»). È una differenza dalla
// griglia, dove `GET /events` guasto dà un elenco vuoto in silenzio.
//
// ⚠️ LO STESSO INSIEME DI SQUADRE DELLA GRIGLIA (allineamento Fetta 3, su
// decisione del committente). Il backend elenca gli allenatori di una squadra
// con `allenaLaSquadra`: persona ATTIVA al primo livello (status assente =
// ACTIVE), sole tessere COACH ATTIVE dell'anno che nominano la squadra
// ESPLICITAMENTE — «tutte le squadre» da sola non ne nomina nessuna, e NON
// toglie le altre. Il lettore client `squadreDelSoggetto` applica il metro
// delle tessere (COACH, ACTIVE, anno) ma non lo status di persona, e col
// default `assorbeTutte` una tessera `allTeams` ASSORBE gli altri nomi: le
// due cinture che chiudono lo scarto stanno in `squadre` (v. sotto), provate
// dal banco caso per caso.
//
// ⚠️ UN RECORD IL CUI EVENTO NON C'È PIÙ (cancellato dopo la segnatura) NON
// COMPARE e non conta né in X né in Y: le righe vengono da `/events`, non dai
// record. Accettato dal committente (incarico Fetta 3, §3).
//
// ⚠️ IL 409 «anno non leggibile» si dice con le parole della scheda del socio
// (`MSG_STAGIONE`, verbatim); ogni altro guasto — 403, 404, rete, su
// QUALUNQUE delle tre letture — finisce nello stesso ramo, senza dettaglio:
// distinguerli a schermo direbbe a un estraneo se quell'id esiste.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaCheck, FaRegStickyNote, FaTimes } from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { normalizeRole } from "../auth/access";
import { eDiUnaMiaSquadra, squadreDelSoggetto } from "../utils/squadre";
import { dataEvento, evTimeStamp } from "../utils/calendario";
import "./CoachAttendance.css";

// I due stati, come in CoachAttendance.jsx: etichette a schermo, valore del
// backend (`STATI_PRESENZA_COACH`, server.js), icone Font Awesome.
const STATI = [
  { valore: "present", etichetta: "Present", Icon: FaCheck },
  { valore: "absent", etichetta: "Absent", Icon: FaTimes }
];

const STATO_DI = Object.fromEntries(STATI.map((s) => [s.valore, s]));

// TESTI A SCHERMO (inglese, CLAUDE.md §3.7). Il testo del 409 e quello
// d'attesa sono quelli di CoachAttendance.jsx, VERBATIM; la frase d'errore è
// la gemella di `MSG_SQUADRA`, per la persona invece che per la squadra.
const MSG_STAGIONE = "Could not read the club season. Please try again in a moment.";
const MSG_ATTESA = "Loading coach attendance…";
const MSG_COACH = "This coach's attendance is not available.";

// Dove si torna quando la scheda è la PRIMA voce della cronologia (indirizzo
// digitato, segnalibro, ricaricamento): la pagina della segnatura, che è
// l'unico posto da cui si arriva qui per clic.
const ROTTA_DI_RIPIEGO = "/coach-attendance";

// ===========================================================================
// AIUTANTI DI DATA — `dataEvento` ed `evTimeStamp` dalla sede unica
// utils/calendario.js (giro G-K1)
// ===========================================================================

/** Il tipo dell'evento, col metro del calendario (MyCalendar.jsx). */
const eAllenamento = (ev) => ev && ev.type === "Training";

const uguali = (a, b) => String(a) === String(b);

/** Giorno + data brevi, la stessa forma delle card del calendario. */
function etichettaBreve(ev) {
  const d = dataEvento(ev);
  if (!d) return String(ev?.date || "");

  return `${d.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase()} ${d.toLocaleDateString(
    "en-AU",
    { day: "2-digit", month: "2-digit" }
  )}`;
}

/**
 * L'anno della data dell'evento, letto da `dataEvento` così da valere per le
 * DUE grafie (in `DD-MM-YYYY` l'anno sta in coda, non in testa). `null` se la
 * data non si legge: un evento senza data leggibile non è di nessuna stagione.
 */
function annoDellEvento(ev) {
  const d = dataEvento(ev);
  return d ? d.getFullYear() : null;
}

// ===========================================================================
// IL CANCELLO — componente esterno, senza effetti e senza chiamate
// ===========================================================================
// Il cancello di RUOLO sta a monte (RequireRole in App.jsx). Qui si guardano,
// nell'ordine: il PARAMETRO (senza id non c'è nulla da chiedere: si dice dove
// si sceglie, forma di AttendanceSummary) e la STAGIONE (`seasonStatus` può
// dire "ready" con `operatingSeason` a `null`: si guardano entrambi, come in
// CoachAttendance). Finché uno dei due manca, nessuna chiamata parte — il
// componente interno non è montato.
//
// Gli hook stanno tutti PRIMA di ogni ritorno: il «Back» è una FUNZIONE
// (vedi `tornaIndietro`) e serve anche alle schermate di cortesia.
export default function CoachAttendanceDetail() {
  const { operatingSeason, seasonStatus } = useAuth() || {};
  const { coachId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const idCoach = String(coachId || "").trim();

  const annoOperativo = operatingSeason == null ? null : operatingSeason;
  const stagioneLeggibile = seasonStatus === "ready" && annoOperativo !== null;

  // LA VIA D'USCITA — ripiego di MemberProfileModal.jsx: `location.key ===
  // "default"` è il modo di react-router v6 per dire «questa è la prima voce
  // della cronologia» (indirizzo diretto, segnalibro, ricaricamento). Lì
  // `navigate(-1)` uscirebbe dall'app o non farebbe nulla: si ripiega sulla
  // pagina della segnatura, con `replace` per non lasciare dietro una voce
  // che riporterebbe qui.
  const tornaIndietro = useCallback(() => {
    if (location.key === "default") {
      navigate(ROTTA_DI_RIPIEGO, { replace: true });
    } else {
      navigate(-1);
    }
  }, [location.key, navigate]);

  if (!idCoach) {
    return (
      <HomeShell backTo={tornaIndietro} showSwitch={false}>
        <h1 className="qpsc-page-title">Coach Attendance</h1>
        <p className="qpsc-body-text">
          Open a coach from the Coach Attendance page to see their record.
        </p>
      </HomeShell>
    );
  }

  if (!stagioneLeggibile) {
    return (
      <HomeShell backTo={tornaIndietro} showSwitch={false}>
        <h1 className="qpsc-page-title">Coach Attendance</h1>
        <p className="qpsc-catt-empty">
          {seasonStatus === "loading" ? MSG_ATTESA : MSG_STAGIONE}
        </p>
      </HomeShell>
    );
  }

  return (
    <SchedaAllenatore
      idCoach={idCoach}
      annoOperativo={annoOperativo}
      onBack={tornaIndietro}
    />
  );
}

// ===========================================================================
// LA SCHEDA — montata solo con un id e a stagione leggibile
// ===========================================================================
function SchedaAllenatore({ idCoach, annoOperativo, onBack }) {
  const [coach, setCoach] = useState(null);
  const [events, setEvents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  // "caricamento" | "ok" | "errore"
  const [stato, setStato] = useState("caricamento");
  const [errore, setErrore] = useState("");

  // -----------------------------------------------------------------------
  // LE TRE LETTURE — insieme, per l'id della rotta. Il flag `annullato`
  // (PresenzeDelFiglio) scarta la risposta di un id ormai abbandonato: la
  // pagina può passare da un allenatore a un altro senza smontarsi.
  // -----------------------------------------------------------------------
  useEffect(() => {
    let annullato = false;

    setStato("caricamento");
    setErrore("");
    setCoach(null);
    setEvents([]);
    setAttendance([]);

    const id = encodeURIComponent(idCoach);

    Promise.all([
      api.get(`/users/${id}`),
      api.get("/events"),
      api.get(`/coach-attendance/coach/${id}`)
    ])
      .then(([documento, calendario, presenze]) => {
        if (annullato) return;

        if (!documento || typeof documento !== "object") {
          setErrore(MSG_COACH);
          setStato("errore");
          return;
        }

        setCoach(documento);
        setEvents(Array.isArray(calendario) ? calendario : []);
        setAttendance(Array.isArray(presenze) ? presenze : []);
        setStato("ok");
      })
      .catch((err) => {
        if (annullato) return;

        // Il 409 «stagione illeggibile» si distingue sul solo codice;
        // rifiuto e guasto di rete finiscono nello stesso ramo generico.
        setCoach(null);
        setEvents([]);
        setAttendance([]);
        setErrore(err?.status === 409 ? MSG_STAGIONE : MSG_COACH);
        setStato("errore");
      });

    return () => {
      annullato = true;
    };
  }, [idCoach]);

  // IL NOME. Stessa composizione di PresenzeDelFiglio (`filter(Boolean)`,
  // perché un documento può essere privo di uno dei due campi). A documento
  // non letto resta vuoto.
  const nome = [coach?.firstName, coach?.lastName].filter(Boolean).join(" ").trim();

  // LE SQUADRE CHE ALLENA nell'anno operativo — il lettore unico sulle
  // tessere, con le opzioni di HomeCoach (`ruoli: ["COACH"]`: le sole
  // tessere COACH, non quelle in cui la persona STA da giocatore; doppioni
  // normalizzati: «U12» e «u12» sono una squadra sola) più le DUE CINTURE
  // che fanno coincidere l'elenco con quello della griglia (`allenaLaSquadra`,
  // server.js — allineamento Fetta 3):
  //   · lo STATUS DI PERSONA, prima delle tessere: se il documento non è
  //     ATTIVO al primo livello l'allenatore non ha squadre, quindi nessuna
  //     sessione («0 of 0»). Il metro RICALCA `requireRole` e
  //     `allenaLaSquadra` (server.js: `normalizzaRuolo(status) || "ACTIVE"`,
  //     poi `!== "ACTIVE"`), nella forma già in uso nel client
  //     (MemberProfileModal.jsx, EditMemberModal.jsx): lo status ASSENTE vale
  //     ACTIVE — i documenti storici non hanno il campo — mai un
  //     `status === "ACTIVE"` ingenuo, che li escluderebbe;
  //   · `assorbeTutte: false`: una tessera «tutte le squadre» NON svuota
  //     l'elenco delle tessere esplicite. La griglia elenca il coach nelle
  //     squadre che le sue tessere NOMINANO; «tutte» da sola non ne nomina
  //     nessuna e resta «0 of 0», come là.
  const squadre = useMemo(() => {
    if (stato !== "ok") return [];

    const statusPersona = normalizeRole(coach?.status) || "ACTIVE";
    if (statusPersona !== "ACTIVE") return [];

    const { nomi } = squadreDelSoggetto(coach?.contexts, annoOperativo, {
      ruoli: ["COACH"],
      assorbeTutte: false,
      doppioni: "normalizzati"
    });
    return nomi;
  }, [coach, stato, annoOperativo]);

  // -----------------------------------------------------------------------
  // LE SESSIONI — di una QUALSIASI delle sue squadre, GIÀ PASSATE, della
  // stagione in corso, dalla più recente: gli stessi tre filtri e lo stesso
  // ordine di `sessioni` in CoachAttendance.jsx, con l'elenco delle squadre
  // al posto della squadra sola. È il denominatore Y.
  // -----------------------------------------------------------------------
  const sessioni = useMemo(() => {
    if (squadre.length === 0) return [];

    const adesso = Date.now();

    return events
      .filter((ev) => eDiUnaMiaSquadra(ev.team, squadre))
      .filter((ev) => annoDellEvento(ev) === annoOperativo)
      .filter((ev) => evTimeStamp(ev) < adesso)
      .sort((a, b) => evTimeStamp(b) - evTimeStamp(a));
  }, [events, squadre, annoOperativo]);

  // -----------------------------------------------------------------------
  // LE CASELLE — per evento, `{ status, note }`, come in `statistiche` di
  // CoachAttendance.jsx. La rotta serve già i soli record di questo
  // allenatore; il confronto su `coachId` via `String()` è la stessa chiave
  // con cui la griglia attribuisce una voce a una persona, non un filtro in
  // più: una voce di un altro id, se mai arrivasse, non si attribuisce.
  // -----------------------------------------------------------------------
  const caselle = useMemo(() => {
    const perEvento = new Map();

    for (const voce of attendance) {
      if (!uguali(voce.coachId, idCoach)) continue;

      perEvento.set(String(voce.eventId), {
        status: voce.status,
        note: typeof voce.note === "string" ? voce.note : ""
      });
    }

    return perEvento;
  }, [attendance, idCoach]);

  const casellaDi = (eventId) => caselle.get(String(eventId)) || null;

  // -----------------------------------------------------------------------
  // IL TOTALE — la formula della griglia, IDENTICA (CoachAttendance.jsx,
  // `statistiche`): present / sessioni.length, arrotondato; il non segnato
  // (come l'assente) non entra nel numeratore ma resta nel denominatore.
  // -----------------------------------------------------------------------
  const totale = useMemo(() => {
    let present = 0;

    for (const ev of sessioni) {
      if (caselle.get(String(ev.id))?.status === "present") present += 1;
    }

    return {
      present,
      percent: sessioni.length ? Math.round((present / sessioni.length) * 100) : 0
    };
  }, [sessioni, caselle]);

  // =========================================================================
  // RESA — colonna standard (intestazione + elenco, non una tabella larga):
  // la stessa di Attendance.jsx.
  // =========================================================================
  return (
    <HomeShell backTo={onBack} showSwitch={false}>
      <h1 className="qpsc-page-title">Coach Attendance</h1>

      {/* DI CHI È QUESTA SCHEDA — forma di `qpsc-att-subject` (Attendance.jsx):
          la barra in testa porta il nome di CHI È COLLEGATO, cioè
          dell'amministratore. Visibile solo se il nome c'è. */}
      {nome && <p className="qpsc-catt-subject">{nome}</p>}

      {stato === "caricamento" ? (
        <p className="qpsc-catt-empty">{MSG_ATTESA}</p>
      ) : stato === "errore" ? (
        <p className="qpsc-catt-error">{errore}</p>
      ) : (
        <>
          {/* IL TOTALE DELLA STAGIONE — riga di contesto della griglia, col
              conto al posto del nome della squadra: «X of Y (Z%)». */}
          <div className="qpsc-catt-context">
            <span className="qpsc-catt-total">
              {totale.present} of {sessioni.length} ({totale.percent}%)
            </span>
            <span className="qpsc-catt-context__meta">
              Season {annoOperativo} &middot; present at past sessions, all teams
            </span>
          </div>

          {sessioni.length === 0 ? (
            <p className="qpsc-catt-empty">No past sessions this season.</p>
          ) : (
            <ElencoSessioni sessioni={sessioni} casellaDi={casellaDi} />
          )}
        </>
      )}
    </HomeShell>
  );
}

// ===========================================================================
// IL SEGNO DI UNA CASELLA — copia da CoachAttendance.jsx (non esportato)
// ===========================================================================
// Verde per la presenza, rosso LEGGIBILE per l'assenza, testo secondario per
// «non segnato»: la coppia già in uso, nessun colore nuovo. Se c'è una nota,
// accanto al segno sta un'icona piccola e il `title` la porta per intero.
function SegnoCasella({ casella }) {
  const voce = casella ? STATO_DI[casella.status] : null;

  if (!voce) {
    return <span className="qpsc-catt-mark qpsc-catt-mark--none">&mdash;</span>;
  }

  const { Icon, etichetta } = voce;
  const nota = casella.note || "";

  return (
    <span
      className={`qpsc-catt-mark qpsc-catt-mark--${casella.status}`}
      title={nota ? `${etichetta} — ${nota}` : etichetta}
    >
      <Icon />
      {nota && (
        <span className="qpsc-catt-mark__note" aria-label="Has a note">
          <FaRegStickyNote />
        </span>
      )}
    </span>
  );
}

/** La pastiglia del tipo evento (TRN/GM), deroga §13.4 (B) come nel modello. */
function PastigliaTipo({ ev }) {
  const training = eAllenamento(ev);

  return (
    <span className={`qpsc-pill qpsc-catt-type--${training ? "training" : "game"}`}>
      {training ? "TRN" : "GM"}
    </span>
  );
}

// ===========================================================================
// L'ELENCO DELLE SESSIONI — righe di SOLA LETTURA
// ===========================================================================
// Una riga per sessione: data breve, SQUADRA (l'elenco mescola più squadre),
// pastiglia del tipo, segno, e la nota a capo se c'è. Forma delle righe di
// dettaglio delle card (`qpsc-catt-detail__row`), ma `<li>` con `<span>`
// dentro, non `<button>`: qui non si apre nessuna modale.
function ElencoSessioni({ sessioni, casellaDi }) {
  return (
    <ul className="qpsc-catt-sessions">
      {sessioni.map((ev) => {
        const casella = casellaDi(ev.id);

        return (
          <li key={ev.id} className="qpsc-catt-session">
            <span className="qpsc-catt-session__date">{etichettaBreve(ev)}</span>
            <span className="qpsc-catt-session__team">{ev.team}</span>
            <PastigliaTipo ev={ev} />
            <SegnoCasella casella={casella} />
            {casella?.note && (
              <span className="qpsc-catt-detail__note">{casella.note}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
